import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { STATUS_POLL_GIVE_UP_MS } from '@lsc/shared';
import { registrationStatusQuery } from './api';
import { isTerminalState, statusPollDelay } from './polling';

/**
 * Registration status with budgeted polling (§51, §52): backoff 3 s → 15 s, paused while the tab
 * is hidden (TanStack default `refetchIntervalInBackground: false`), stops at terminal states and
 * after STATUS_POLL_GIVE_UP_MS (`timedOut` – the page then shows a hint instead of polling).
 */
export function useRegistrationStatus() {
  const [pollRound, setPollRound] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, STATUS_POLL_GIVE_UP_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [pollRound]);

  const query = useQuery({
    ...registrationStatusQuery,
    refetchInterval: (q) => {
      const state = q.state.data?.registration.state;
      if (timedOut || q.state.status === 'error' || (state && isTerminalState(state))) return false;
      return statusPollDelay(q.state.dataUpdateCount);
    },
    // Returning from the mail app is the moment the state most likely changed.
    refetchOnWindowFocus: (q) => {
      const state = q.state.data?.registration.state;
      return !(state && isTerminalState(state));
    },
  });

  const { refetch } = query;
  const restartPolling = useCallback(() => {
    setTimedOut(false);
    setPollRound((round) => round + 1);
    void refetch();
  }, [refetch]);

  return { query, timedOut, restartPolling };
}

/**
 * Whole seconds until `targetIso` (0 when reached or null); re-renders once per second.
 * The start time is taken on mount – key the calling component by `targetIso`.
 */
export function useSecondsUntil(targetIso: string | null): number {
  const target = targetIso === null ? 0 : Date.parse(targetIso);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target <= Date.now()) return undefined;
    const interval = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= target) clearInterval(interval);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [target]);

  return Math.max(0, Math.ceil((target - now) / 1000));
}
